import React from 'react';
import {
  Card,
  CardContent,
  Typography,
  Box,
  Avatar,
  alpha,
  Tabs,
  Tab
} from '@mui/material';
import { Groups, People, ChatBubble } from '@mui/icons-material';
import { Community } from '../../types/community';
import { CommunityListSkeleton } from '../skeletons';

interface CommunityListProps {
  communities: Community[];
  loading: boolean;
  activeTab: number;
  setActiveTab: (value: number) => void;
  isDark: boolean;
  goldColor: string;
  deepGreen: string;
  navigate: (path: string) => void;
  glassEffect: any;
  user: any;
}

const CommunityList: React.FC<CommunityListProps> = ({
  communities,
  loading,
  activeTab,
  setActiveTab,
  isDark,
  goldColor,
  deepGreen,
  navigate,
  glassEffect,
  user
}) => {
  return (
    <Card sx={{
      borderRadius: '20px',
      ...glassEffect,
      height: { xs: 'auto', md: 'calc(100vh - 20px)' },
      overflowY: 'auto',
      top: { xs: 0, md: 20 }
    }}>
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {/* Tabs Section - Minimal styling */}
        <Box sx={{ 
          mb: 2,
          borderBottom: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.1)' : 'rgba(5, 24, 20, 0.1)'}`,
    top: 0,
    zIndex: 10,
        }}>
          <Tabs
            value={activeTab}
            onChange={(_, value) => setActiveTab(value)}
            variant="scrollable"
            scrollButtons="auto"
            allowScrollButtonsMobile
            sx={{
              minHeight: '40px',
              // The three labels can exceed a narrow column; keep every tab reachable
              // instead of clipping the last one.
              '& .MuiTabs-scrollButtons.Mui-disabled': { opacity: 0.3 },
              '& .MuiTab-root': {
                color: isDark ? 'rgba(255, 255, 255, 0.7)' : 'rgba(5, 24, 20, 0.7)',
                fontWeight: 600,
                textTransform: 'none',
                // The column is at its narrowest at the start of each breakpoint
                // (900 and 1200), so size the labels for those, not the midpoint.
                fontSize: { xs: '0.875rem', sm: '1.1rem', md: '0.68rem', lg: '0.68rem', xl: '0.85rem' },
                padding: { xs: '6px 12px', sm: '8px 16px', md: '8px 4px', lg: '8px 4px', xl: '8px 7px' },
                minHeight: '50px',
                minWidth: 'auto',
                '&.Mui-selected': {
                  color: isDark ? goldColor : deepGreen,
                  fontWeight: 700,
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: goldColor,
                height: '3px',
                borderRadius: '3px 3px 0 0',

                boxShadow: `0 0 8px ${alpha(goldColor, 0.8)}, 
                0 0 16px ${alpha(goldColor, 0.5)}`,
              }
            }}
          >
            <Tab label="Discover" />
            <Tab label="My Communities" disabled={!user} />
            <Tab label="Trending" />
          </Tabs>
        </Box>

        {/* Communities List */}
        <Typography variant="h6" sx={{ 
          mb: 3, 
          fontWeight: 700, 
          display: 'flex', 
          alignItems: 'center', 
          gap: 1, 
          color: isDark ? '#fff' : deepGreen,
          fontSize: { xs: '1rem', sm: '1.125rem' }
        }}>
          <Groups sx={{ color: goldColor, fontSize: { xs: '1.25rem', sm: '1.5rem' } }} />
          {activeTab === 0 ? 'All Communities' : 
           activeTab === 1 ? 'My Communities' : 
           'Trending Communities'}
        </Typography>

        {loading ? (
          <CommunityListSkeleton />
        ) : communities.length === 0 ? (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            <Groups sx={{ fontSize: 30, color: goldColor, mb: 1, opacity: 0.5 }} />
            <Typography variant="body2" sx={{ 
              color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
              fontSize: { xs: '0.75rem', sm: '0.875rem' }
            }}>
              {activeTab === 0 ? 'No communities found' : 
               activeTab === 1 ? 'Join some communities!' : 
               'No trending communities'}
            </Typography>
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {communities.map((community) => (
              <Box
                key={community.id}
                onClick={() => navigate(`/community/${community.slug}`)}
                sx={{
                  cursor: 'pointer',
                  borderRadius: '16px',
                  overflow: 'hidden',
                  position: 'relative',
                  transition: 'all 0.2s',
                  ...glassEffect,
                  border: '1px solid transparent',
                  '&:hover': {
                    transform: { xs: 'none', sm: 'translateY(-2px)' },
                    borderColor: goldColor
                  }
                }}
              >
                {/* Banner */}
                <Box sx={{
                  height: { xs: 80, sm: 100 },
                  position: 'relative',
                  background: community.bannerImage
                    ? `url(${community.bannerImage}) center/cover no-repeat`
                    : isDark
                      ? 'linear-gradient(135deg, #C4B66F 0%, #0F3B2E 100%)'
                      : 'linear-gradient(135deg, #051814 0%, #1D5C4A 100%)',
                }}>
                  {/* Profile picture positioned on banner */}
                  <Avatar
                    src={community.profilePicture}
                    sx={{
                      position: 'absolute',
                      bottom: -20,
                      left: 16,
                      width: { xs: 48, sm: 56 },
                      height: { xs: 48, sm: 56 },
                      bgcolor: goldColor,
                      color: deepGreen,
                      fontWeight: 700,
                      fontSize: { xs: '1rem', sm: '1.2rem' },
                      border: '3px solid',
                      borderColor: glassEffect.background as string,
                      boxShadow: '0 4px 20px rgba(0,0,0,0.3)',
                      zIndex: 2
                    }}
                  >
                    {community.name.charAt(0).toUpperCase()}
                  </Avatar>
                </Box>

                <CardContent sx={{
                  p: { xs: 1.5, sm: 2.5 },
                  pt: { xs: 3, sm: 4 } // Extra padding to accommodate profile picture overlap
                }}>
                  <Box sx={{ mb: { xs: 1, sm: 2 } }}>
                    <Typography variant="subtitle1" sx={{
                      fontWeight: 800,
                      color: isDark ? '#fff' : deepGreen,
                      lineHeight: 1.2,
                      fontSize: { xs: '0.85rem', sm: '0.95rem' },
                      mb: 0.5
                    }}>
                      wfh/{community.slug}
                    </Typography>
                    <Typography variant="caption" sx={{
                      color: isDark ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.6)',
                      fontSize: { xs: '0.7rem', sm: '0.8rem' },
                      display: 'block',
                      lineHeight: 1.3
                    }}>
                      {community.description?.length > 60
                        ? `${community.description.substring(0, 60)}...`
                        : community.description || 'No description'}
                    </Typography>
                  </Box>

                  <Box sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mt: { xs: 1.5, sm: 2 },
                    pt: { xs: 1.5, sm: 2 },
                    borderTop: `1px solid ${alpha(goldColor, 0.1)}`
                  }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 1.5 } }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <People sx={{ fontSize: { xs: 18, sm: 20 }, color: goldColor }} />
                        <Typography variant="caption" sx={{
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.95rem' }
                        }}>
                          {community.membersCount.toLocaleString()}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ChatBubble sx={{ fontSize: { xs: 18, sm: 20 }, color: goldColor }} />
                        <Typography variant="caption" sx={{
                          color: isDark ? 'rgba(255,255,255,0.7)' : 'rgba(0,0,0,0.7)',
                          fontWeight: 600,
                          fontSize: { xs: '0.85rem', sm: '0.95rem' }
                        }}>
                          {community.postsCount}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </CardContent>
              </Box>
            ))}
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

export default CommunityList;